import { useState, useMemo, useEffect, useRef, Suspense } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useConfig } from '../../contexts/ConfigContext';
import { canAccessMenuItem } from '../../config/permissions';
import { navGroups, allNavItems } from '../../config/navigation';
import type { NavItem } from '../../config/navigation';
import usePullToRefresh from '../../hooks/usePullToRefresh';
import ActionBar from '../app/components/ActionBar';
import RewardSpeedDial from '../app/components/RewardSpeedDial';
import MobileCreateVoucherModal from '../app/components/MobileCreateVoucherModal';
import MobileReferralShareModal from '../app/components/MobileReferralShareModal';
import AskPanel from '../../components/ask/AskPanel';
import { AskSparkle } from '../../components/ask/AskSparkle';
import { useAskAccess } from '../../components/ask/useAskAccess';
import { KNOWN_COUNTRIES, HQ_PORTAL } from '../../lib/countries';
import { useSidebarHidden } from '../../hooks/useSidebarHidden';
import { PanelIcon } from '../../components/PanelIcon';
import { CHATS_WINDOW, chatsUrl } from '../../lib/chatsLink';
import { STAFF_ASSISTANT_NAME } from '../../config/assistant';

// Collapsible nav section. Only one is open at a time: the one holding the current page,
// or the one last opened (Gary, 1 Nov 2026: one list, sections fold away).
function NavSection({
  title,
  icon,
  children,
  isOpen,
  onToggle,
}: {
  title: string;
  icon: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="nav-section">
      <button
        type="button"
        className={`nav-section-toggle ${isOpen ? 'open' : ''}`}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="nav-section-icon">{icon}</span>
        <span className="nav-section-title">{title}</span>
        <span className="nav-section-arrow">{isOpen ? '\u25BC' : '\u25B6'}</span>
      </button>
      {isOpen && <div className="nav-section-items">{children}</div>}
    </div>
  );
}

/** Does this menu item own the current page? */
function matchesPath(item: NavItem, pathname: string): boolean {
  return item.end ? pathname === item.path : pathname.startsWith(item.path);
}

const DEFAULT_PINS = ['/to-do', '/inbox'];
// Sections kept at the bottom of the sidebar, above the user menu.
const FOOTER_SECTIONS = ['admin', 'superAdmin'];
const pinsKey = (userId: string) => `sentinel_nav_pins_${userId}`;

function loadPins(userId: string | undefined): string[] {
  if (!userId) return DEFAULT_PINS;
  try {
    const raw = localStorage.getItem(pinsKey(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) && parsed.every((p) => typeof p === 'string') ? (parsed as string[]) : DEFAULT_PINS;
  } catch {
    return DEFAULT_PINS;
  }
}

// Country switcher — country portals first, then HQ. Source: src/lib/countries.ts
const COUNTRY_SITES = [...KNOWN_COUNTRIES, HQ_PORTAL];

export default function AdminLayout() {
  const { user, appMetadata, signOut, getPortalAccess, supabase } = useAuth();
  const { countryCode, config } = useConfig();
  // CMB Chats is a country's WhatsApp inbox: the switch shows where it is switched on (Gary, 18 Oct).
  const chatsOn = config?.messaging_inbox_enabled === true;
  const [countrySwitcherOpen, setCountrySwitcherOpen] = useState(false);
  // POS Leads is head-office by default. A non-head-office user also gets the nav
  // item once at least one lead is assigned to them (RLS limits them to those).
  const [hasAssignedPosLeads, setHasAssignedPosLeads] = useState(false);
  const countrySwitcherRef = useRef<HTMLDivElement>(null);
  const portalAccess = getPortalAccess();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const { hidden: sidebarHidden, toggle: toggleSidebar } = useSidebarHidden();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showSpeedDial, setShowSpeedDial] = useState(false);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showAsk, setShowAsk] = useState(false);
  const canAsk = useAskAccess();
  const userMenuRef = useRef<HTMLDivElement>(null);
  // Disable pull-to-refresh on form pages (causes accidental refresh mid-form)
  const isFormPage = /\/(service-logs\/(new|\w{8}-)|bookings\/(new|\w{8}-))/.test(location.pathname);
  // The Ask page is a full-bleed chat with its own scrolling thread: no page padding, no pull-to-refresh.
  const isAskPage = location.pathname === '/ask' || location.pathname === '/inbox' || location.pathname === '/marketing/studio';
  const { isRefreshing, pullIndicatorProps } = usePullToRefresh(isFormPage || isAskPage ? '__disabled__' : '.content-area');

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Close user menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [userMenuOpen]);

  // Close country switcher on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        countrySwitcherRef.current &&
        !countrySwitcherRef.current.contains(event.target as Node)
      ) {
        setCountrySwitcherOpen(false);
      }
    };
    if (countrySwitcherOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [countrySwitcherOpen]);

  // Does this non-head-office user have any POS leads assigned? Drives the nav item.
  useEffect(() => {
    const isHeadOffice = appMetadata?.is_super_admin || appMetadata?.scope === 'all' || appMetadata?.scope === 'global';
    if (!user || isHeadOffice) { setHasAssignedPosLeads(false); return; }
    let active = true;
    supabase
      .from('pos_prospect_properties')
      .select('id', { count: 'exact', head: true })
      .eq('assigned_to', user.id)
      .then(({ count }) => { if (active) setHasAssignedPosLeads((count ?? 0) > 0); },
            () => { if (active) setHasAssignedPosLeads(false); });
    return () => { active = false; };
  }, [user, appMetadata, supabase]);

  // Filter nav items based on permissions
  const filterItems = (items: NavItem[]) => {
    if (!user) return [];
    if (appMetadata?.is_super_admin) return items;
    // Brand Collaborations has no per-feature permission key. It is gated by
    // Country Admin scope OR voucher-manage: collaboration codes are the
    // commercial element of a voucher, so a voucher manager owns that already.
    // This mirrors the server-side canManageCollaborations gate.
    const isCountryAdmin = appMetadata?.scope === 'all' || appMetadata?.scope === 'global';
    const canManageVouchers = user.permissions?.vouchers?.level === 'manage';
    return items.filter(
      (item) =>
        // CMB Chats: anyone who uses Sentinel, where WhatsApp is switched on (as the Chats app itself).
        (item.path === '/inbox' ? chatsOn : false) ||
        canAccessMenuItem(item.path, user) ||
        ((isCountryAdmin || canManageVouchers) && item.path === '/collaborations') ||
        // POS Leads: head office (country admins; super admins bypass above) OR a
        // user who has at least one lead assigned to them (matches the DB
        // is_country_admin_or_super OR assignee RLS path).
        ((isCountryAdmin || hasAssignedPosLeads) && item.path === '/pos-leads') ||
        // Customer Survey: head office only, no per-feature permission key.
        // survey_responses is RLS-gated to is_country_admin_or_super(), so the
        // database is the real gate and this just mirrors it in the sidebar.
        (isCountryAdmin && item.path === '/reports/customer-survey') ||
        // Marketing Hub: head office only, no per-feature permission key yet.
        // Mirrors the gate on the page itself (super admin, handled above, or
        // country-admin scope).
        (isCountryAdmin && item.path.startsWith('/marketing/')) ||
        // Help guides: every signed-in user, no permission key. They explain what
        // a code costs and how a tier moves, which is exactly what an operator
        // needs to read, so gating them would defeat the point.
        item.path.startsWith('/help'),
    );
  };

  const filteredNavGroups = useMemo(() => {
    // Every section in navigation.ts, each filtered by the person's permissions.
    // Super Admin shows only to super admins.
    const out: Record<string, { title: string; icon: string; items: NavItem[] }> = {};
    for (const [key, g] of Object.entries(navGroups)) {
      out[key] = {
        title: g.title,
        icon: g.icon,
        items: key === 'superAdmin' ? (appMetadata?.is_super_admin ? g.items : []) : filterItems(g.items),
      };
    }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, appMetadata, hasAssignedPosLeads]);

  const sectionKeys = useMemo(
    () => Object.keys(filteredNavGroups).filter((k) => (filteredNavGroups[k]?.items.length ?? 0) > 0),
    [filteredNavGroups],
  );

  // The section that holds the current page (longest matching path wins).
  const activeSection = useMemo(() => {
    let best: { len: number; key: string } | null = null;
    for (const key of sectionKeys) {
      for (const item of filteredNavGroups[key]?.items ?? []) {
        if (matchesPath(item, location.pathname) && (!best || item.path.length > best.len)) {
          best = { len: item.path.length, key };
        }
      }
    }
    return best?.key ?? null;
  }, [sectionKeys, filteredNavGroups, location.pathname]);

  // One section open at a time; moving to a page opens its section.
  const [openSection, setOpenSection] = useState<string | null>(null);
  useEffect(() => {
    if (activeSection) setOpenSection(activeSection);
  }, [activeSection]);
  const toggleSection = (key: string) => setOpenSection((cur) => (cur === key ? null : key));

  // Pinned pages, each person's own, kept on this device (Gary, 1 Nov).
  const [pins, setPins] = useState<string[]>(() => loadPins(user?.id));
  useEffect(() => { setPins(loadPins(user?.id)); }, [user?.id]);
  const togglePin = (path: string) => {
    setPins((cur) => {
      const next = cur.includes(path) ? cur.filter((p) => p !== path) : [...cur, path];
      if (user?.id) {
        try { localStorage.setItem(pinsKey(user.id), JSON.stringify(next)); } catch { /* private mode: pins last this visit */ }
      }
      return next;
    });
  };
  const allowedItems = useMemo(
    () => sectionKeys.flatMap((k) => (filteredNavGroups[k]?.items ?? []).map((item) => ({ item, section: filteredNavGroups[k]!.title }))),
    [sectionKeys, filteredNavGroups],
  );
  const pinnedItems = useMemo(
    () => pins.map((p) => allowedItems.find((a) => a.item.path === p)?.item).filter((i): i is NavItem => !!i),
    [pins, allowedItems],
  );

  // Search: Ctrl K or Cmd K jumps to the box; type to find any page you can open.
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setMenuOpen(true);
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allowedItems.filter((a) => a.item.label.toLowerCase().includes(q) || a.section.toLowerCase().includes(q) || (a.item.sub ?? '').toLowerCase().includes(q));
  }, [query, allowedItems]);
  const goTo = (path: string) => {
    setQuery('');
    setMenuOpen(false);
    navigate(path);
  };

  const renderLink = (item: NavItem, extraClass: string) => (
    <div key={item.path} className="nav-row">
      <NavLink
        to={item.path}
        end={item.end}
        // On a phone Chats opens the stand-alone app (the side menu's own Chats switch below), so phones have
        // one Chats entry that gets alerts (Gary, 19 Oct: Debbie signed in to the copy inside Sentinel).
        className={({ isActive }) =>
          `nav-link ${extraClass} ${isActive ? 'active' : ''}${item.path === '/inbox' ? ' hide-mobile' : ''}`
        }
      >
        <span>{item.icon}</span>
        <span>{item.label}</span>
      </NavLink>
      <button
        type="button"
        className={`nav-pin${pins.includes(item.path) ? ' pinned' : ''}`}
        onClick={() => togglePin(item.path)}
        aria-label={pins.includes(item.path) ? `Unpin ${item.label}` : `Pin ${item.label}`}
        title={pins.includes(item.path) ? 'Unpin' : 'Pin to the top'}
      >
        {pins.includes(item.path) ? '\u2605' : '\u2606'}
      </button>
    </div>
  );

  const renderGroup = (key: string) => {
    const group = filteredNavGroups[key];
    if (!group || group.items.length === 0) return null;
    const plain = group.items.filter((i) => !i.sub);
    const subs: { title: string; items: NavItem[] }[] = [];
    for (const item of group.items) {
      if (!item.sub) continue;
      const found = subs.find((s) => s.title === item.sub);
      if (found) found.items.push(item);
      else subs.push({ title: item.sub, items: [item] });
    }
    return (
      <NavSection
        key={key}
        title={group.title}
        icon={group.icon}
        isOpen={openSection === key}
        onToggle={() => toggleSection(key)}
      >
        {plain.map((item) => renderLink(item, 'nav-link-nested'))}
        {subs.map((sg) => (
          <div key={sg.title} className="nav-subgroup">
            <div className="nav-subgroup-title">{sg.title}</div>
            {sg.items.map((item) => renderLink(item, 'nav-link-subgrouped'))}
          </div>
        ))}
      </NavSection>
    );
  };

  const currentPage = allNavItems.find((item) =>
    item.end
      ? location.pathname === item.path
      : location.pathname.startsWith(item.path),
  );

  const userInitial =
    user?.first_name?.[0]?.toUpperCase() ||
    user?.email?.[0]?.toUpperCase() ||
    'U';

  return (
    <div className={`portal-layout${sidebarHidden ? ' sidebar-hidden' : ''}`}>
      {/* Mobile overlay */}
      <div
        className={`sidebar-overlay ${menuOpen ? 'open' : ''}`}
        onClick={() => setMenuOpen(false)}
      />

      <aside className={`sidebar ${menuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div style={{ position: 'relative' }} ref={countrySwitcherRef}>
            {appMetadata?.is_super_admin ? (
              <button
                className="sidebar-brand"
                onClick={() => setCountrySwitcherOpen(!countrySwitcherOpen)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', font: 'inherit', padding: 0, display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                CleanMyBed - {countryCode}
                <span style={{ fontSize: '10px' }}>{countrySwitcherOpen ? '\u25B2' : '\u25BC'}</span>
              </button>
            ) : (
              <div className="sidebar-brand">CleanMyBed - {countryCode}</div>
            )}
            {/* App switcher (Gary, 1 Nov): Sentinel and CMB Chats side by side, top left, on every screen size. */}
            {chatsOn ? (
              <div className="app-switcher" role="group" aria-label="Switch app">
                <span className="app-switcher-item active" aria-current="page">Sentinel</span>
                <a className="app-switcher-item" href={chatsUrl(countryCode, location.pathname, location.search)} target={CHATS_WINDOW}
                  rel="noopener" title="Switch to CMB Chats (this customer's chat when one is open)">
                  CMB Chats
                </a>
              </div>
            ) : (
              <div className="sidebar-subtitle">Sentinel Business Management</div>
            )}
            {countrySwitcherOpen && appMetadata?.is_super_admin && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '4px',
                background: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 100,
                minWidth: '180px',
                overflow: 'hidden',
              }}>
                {COUNTRY_SITES.map(site => (
                  <a
                    key={site.code}
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setCountrySwitcherOpen(false)}
                    style={{
                      display: 'block',
                      padding: '10px 16px',
                      fontSize: '13px',
                      color: site.code === countryCode ? '#772cbb' : '#333',
                      fontWeight: site.code === countryCode ? 600 : 400,
                      textDecoration: 'none',
                      background: site.code === countryCode ? '#f3e8ff' : 'transparent',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    {site.label} ({site.code})
                  </a>
                ))}
              </div>
            )}
          </div>
          <button
            className="sidebar-close"
            onClick={() => setMenuOpen(false)}
          >
            ✕
          </button>
        </div>

        {/* Search every page you can open (Ctrl K or Cmd K) */}
        <div className="sidebar-search">
          <input
            ref={searchRef}
            type="search"
            className="sidebar-search-input"
            placeholder="Find a page (Ctrl K)"
            aria-label="Find a page"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && results[0]) goTo(results[0].item.path);
              if (e.key === 'Escape') setQuery('');
            }}
          />
        </div>

        <nav className="sidebar-nav">
          {query.trim() ? (
            results.length ? (
              results.map(({ item, section }) => (
                <button key={item.path} type="button" className="nav-link nav-search-result" onClick={() => goTo(item.path)}>
                  <span>{item.icon}</span>
                  <span className="nav-search-label">{item.label}</span>
                  <span className="nav-search-section">{section}</span>
                </button>
              ))
            ) : (
              <p className="nav-search-empty">No page matches.</p>
            )
          ) : (
            <>
              <NavLink to="/" end className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                <span>🏠</span>
                <span>Home</span>
              </NavLink>
              {pinnedItems.length > 0 && (
                <div className="nav-pinned">
                  <div className="nav-subgroup-title">Pinned</div>
                  {pinnedItems.map((item) => renderLink(item, 'nav-link-nested'))}
                </div>
              )}
              {sectionKeys.filter((k) => !FOOTER_SECTIONS.includes(k) && k !== 'help').map((key) => renderGroup(key))}
              {sectionKeys.includes('help') && <div className="nav-phone-only">{renderGroup('help')}</div>}
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          {/* Admin and Super Admin pinned to the bottom (Gary, 1 Nov) */}
          {sectionKeys.filter((k) => FOOTER_SECTIONS.includes(k)).map((key) => (
            <div key={key} className="pinned-section">{renderGroup(key)}</div>
          ))}
          {/* User menu with dropdown */}
          <div className="user-menu-container" ref={userMenuRef}>
            <button
              className="user-menu-trigger"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <div className="user-avatar">{userInitial}</div>
              <div className="user-menu-info">
                <div className="user-name">
                  {user?.first_name || 'User'}
                </div>
                <div className="user-email">{user?.email || ''}</div>
              </div>
              <span className="user-menu-arrow">
                {userMenuOpen ? '\u25B2' : '\u25BC'}
              </span>
            </button>

            {userMenuOpen && (
              <div className="user-menu-dropdown">
                <Link
                  to="/profile"
                  className="user-menu-item"
                  onClick={() => setUserMenuOpen(false)}
                >
                  <span>👤</span>
                  <span>Profile</span>
                </Link>
                <Link
                  to="/my-availability"
                  className="user-menu-item"
                  onClick={() => setUserMenuOpen(false)}
                >
                  <span>📅</span>
                  <span>My Availability</span>
                </Link>
                <div className="user-menu-item user-menu-item-disabled" title="Coming soon">
                  <span>⚙️</span>
                  <span>Settings</span>
                  <span className="coming-soon-badge">Soon</span>
                </div>
                {portalAccess.ambassador && (
                  <>
                    <div className="user-menu-divider" />
                    <Link
                      to="/ambassador"
                      className="user-menu-item"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <span>⭐</span>
                      <span>Ambassador Portal</span>
                    </Link>
                  </>
                )}
                <div className="user-menu-divider" />
                <button
                  className="user-menu-item user-menu-signout"
                  onClick={signOut}
                >
                  <span>🚪</span>
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      <main className="main-content">
        <header className="top-bar">
          <button
            className="mobile-menu-toggle"
            onClick={() => setMenuOpen(true)}
            title="Menu"
          >
            <span>&#9776;</span>
          </button>
<button
            type="button"
            className="sidebar-toggle"
            onClick={toggleSidebar}
            title={sidebarHidden ? 'Show menu (Ctrl+B)' : 'Hide menu (Ctrl+B)'}
            aria-label={sidebarHidden ? 'Show menu' : 'Hide menu'}
            aria-pressed={sidebarHidden}
          >
            <PanelIcon />
          </button>
          <h1 className="page-title">
            {currentPage?.label || 'Portal'}
          </h1>

          {/* Quick Actions — mirrors Global's topbar buttons */}
          <div className="desktop-quick-actions">
            <button
              className="desktop-action-btn"
              onClick={() => setShowSpeedDial(true)}
              title="Voucher or Referral"
            >
              <span className="desktop-action-icon">{'\uD83C\uDF81'}</span>
              <span className="desktop-action-label">Reward</span>
            </button>
            <Link to="/bookings?create=true" className="desktop-action-btn" title="New booking">
              <span className="desktop-action-icon">{'\uD83D\uDCC5'}</span>
              <span className="desktop-action-label">Book</span>
            </Link>
            <Link to="/service-logs/new" className="desktop-action-btn" title="New Service Log">
              <span className="desktop-action-icon">{'\uD83D\uDCCB'}</span>
              <span className="desktop-action-label">Log</span>
            </Link>
            <Link to="/certification/submit" className="desktop-action-btn" title="Submit Certification">
              <span className="desktop-action-icon">{'\uD83D\uDCDC'}</span>
              <span className="desktop-action-label">Cert</span>
            </Link>
            {canAsk && (
              <button className="desktop-action-btn" onClick={() => setShowAsk(true)} title={`Ask ${STAFF_ASSISTANT_NAME}`}>
                <span className="desktop-action-icon"><AskSparkle /></span>
                <span className="desktop-action-label">Ask</span>
              </button>
            )}
            {/* Help guides in the top bar, next to Ask (Gary, 1 Nov); phones keep it in the side menu. */}
            <Link to="/help" className="desktop-action-btn" title="Help guides">
              <span className="desktop-action-icon">{'\u2753'}</span>
              <span className="desktop-action-label">Help</span>
            </Link>
          </div>

          <div className="top-bar-actions">
            <div id="page-actions"></div>
          </div>
        </header>

        {/* Pull-to-refresh indicator (mobile PWA) */}
        <div className="pull-indicator" {...pullIndicatorProps}>
          <div className="pull-indicator-spinner" />
          <span className="pull-indicator-text">
            {isRefreshing ? 'Refreshing…' : 'Pull to refresh'}
          </span>
        </div>

        <div className={`content-area${isAskPage ? ' content-area--flush' : ''}`}>
          {/* Suspense with null fallback: lazy chunks are 3–15 KB gzipped so
              download is ~100–200ms on 4G. The page's own loading spinner
              becomes the single visible loading state — no chunk-spinner then
              page-spinner jump. See ADR-005 revision 2026-04-18. */}
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        </div>
      </main>

      {/* Mobile Action Bar — visible on mobile only */}
      <ActionBar onShowReward={() => setShowSpeedDial(true)} onShowAsk={canAsk ? () => setShowAsk(true) : undefined} />

      {/* Dr Cleanstein side panel */}
      {showAsk && <AskPanel onClose={() => setShowAsk(false)} />}

      {/* Reward Speed Dial */}
      {showSpeedDial && (
        <RewardSpeedDial
          onClose={() => setShowSpeedDial(false)}
          onSelectVoucher={() => {
            setShowSpeedDial(false);
            setShowVoucherModal(true);
          }}
          onSelectReferral={() => {
            setShowSpeedDial(false);
            setShowReferralModal(true);
          }}
        />
      )}

      {/* Create Voucher Modal */}
      {showVoucherModal && (
        <MobileCreateVoucherModal
          onClose={() => setShowVoucherModal(false)}
          onSuccess={() => setShowVoucherModal(false)}
        />
      )}

      {/* Share Referral Modal */}
      {showReferralModal && (
        <MobileReferralShareModal
          onClose={() => setShowReferralModal(false)}
        />
      )}
    </div>
  );
}
